import React, { useState, useEffect } from 'react';
import { masterDataApi, API_BASE_URL } from '../../services/api';
import { handleApiError, showSuccess } from '../../utils/handleApiError';
import ConfirmModal from '../ConfirmModal';

interface Feed {
  id: number;
  name: string;
  type: string;
  protein_content?: number;
  energy_content?: number;
  manufacturer?: string;
  description?: string;
  minimum_stock_alert_kg?: number;
}

const FeedManagement: React.FC = () => {
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFeed, setEditingFeed] = useState<Feed | null>(null);
  const [feedTypes, setFeedTypes] = useState<any[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [newFeed, setNewFeed] = useState({
    name: '',
    type: '',
    protein_content: '',
    energy_content: '',
    manufacturer: '',
    description: '',
    minimum_stock_alert_kg: ''
  });

  useEffect(() => {
    loadFeeds();
    loadFeedTypes();
  }, []);

  const loadFeeds = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/feeds`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFeeds(data);
      }
    } catch (error) {
      console.error('Error loading feeds:', error);
      handleApiError(error, 'Hiba történt a takarmányok betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const loadFeedTypes = async () => {
    try {
      const category = await masterDataApi.getCategory('feed_types');
      setFeedTypes(category.values || []);
    } catch (error) {
      console.error('Error loading feed types:', error);
      handleApiError(error, 'Hiba történt a takarmány típusok betöltésekor');
    }
  };

  const handleAdd = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/feeds`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newFeed,
          protein_content: newFeed.protein_content ? parseFloat(newFeed.protein_content) : null,
          energy_content: newFeed.energy_content ? parseFloat(newFeed.energy_content) : null,
          minimum_stock_alert_kg: newFeed.minimum_stock_alert_kg ? parseFloat(newFeed.minimum_stock_alert_kg) : 0
        })
      });

      if (response.ok) {
        showSuccess('Takarmány sikeresen hozzáadva');
        setShowAddModal(false);
        setNewFeed({
          name: '',
          type: '',
          protein_content: '',
          energy_content: '',
          manufacturer: '',
          description: '',
          minimum_stock_alert_kg: ''
        });
        loadFeeds();
      }
    } catch (error) {
      console.error('Error adding feed:', error);
      handleApiError(error, 'Hiba történt a takarmány hozzáadásakor');
    }
  };

  const handleEdit = async () => {
    if (!editingFeed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/feeds/${editingFeed.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingFeed)
      });

      if (response.ok) {
        showSuccess('Takarmány sikeresen frissítve');
        setEditingFeed(null);
        loadFeeds();
      }
    } catch (error) {
      console.error('Error updating feed:', error);
      handleApiError(error, 'Hiba történt a takarmány frissítésekor');
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;

    try {
      const response = await fetch(`${API_BASE_URL}/feeds/${deleteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showSuccess('Takarmány sikeresen törölve');
        loadFeeds();
      }
    } catch (error) {
      console.error('Error deleting feed:', error);
      handleApiError(error, 'Hiba történt a takarmány törlésekor');
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Takarmány kezelés</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
        >
          + Új takarmány
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Név
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Típus
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fehérje %
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Energia
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Gyártó
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Műveletek
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {feeds.map((feed) => (
              <tr key={feed.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {feed.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {feed.type}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {feed.protein_content ? `${feed.protein_content}%` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {feed.energy_content || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {feed.manufacturer || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => setEditingFeed(feed)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    Szerkesztés
                  </button>
                  <button
                    onClick={() => setDeleteId(feed.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Törlés
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Új takarmány hozzáadása</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Név *</label>
                <input
                  type="text"
                  value={newFeed.name}
                  onChange={(e) => setNewFeed({...newFeed, name: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Típus *</label>
                <select
                  value={newFeed.type}
                  onChange={(e) => setNewFeed({...newFeed, type: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  required
                >
                  <option value="">Válassz típust...</option>
                  {feedTypes.map((type) => (
                    <option key={type.key} value={type.key}>{type.value}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Fehérje tartalom (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={newFeed.protein_content}
                  onChange={(e) => setNewFeed({...newFeed, protein_content: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Energia tartalom</label>
                <input
                  type="number"
                  step="0.1"
                  value={newFeed.energy_content}
                  onChange={(e) => setNewFeed({...newFeed, energy_content: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Gyártó</label>
                <input
                  type="text"
                  value={newFeed.manufacturer}
                  onChange={(e) => setNewFeed({...newFeed, manufacturer: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Leírás</label>
                <textarea
                  value={newFeed.description}
                  onChange={(e) => setNewFeed({...newFeed, description: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Minimum készlet riasztás (kg)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newFeed.minimum_stock_alert_kg}
                  onChange={(e) => setNewFeed({...newFeed, minimum_stock_alert_kg: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Mégse
              </button>
              <button
                onClick={handleAdd}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Hozzáadás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingFeed && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Takarmány szerkesztése</h3>
              <button
                onClick={() => setEditingFeed(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Név *</label>
                <input
                  type="text"
                  value={editingFeed.name}
                  onChange={(e) => setEditingFeed({...editingFeed, name: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Típus *</label>
                <select
                  value={editingFeed.type}
                  onChange={(e) => setEditingFeed({...editingFeed, type: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  required
                >
                  <option value="">Válassz típust...</option>
                  {feedTypes.map((type) => (
                    <option key={type.key} value={type.key}>{type.value}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Fehérje tartalom (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={editingFeed.protein_content || ''}
                  onChange={(e) => setEditingFeed({...editingFeed, protein_content: e.target.value ? parseFloat(e.target.value) : undefined})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Energia tartalom</label>
                <input
                  type="number"
                  step="0.1"
                  value={editingFeed.energy_content || ''}
                  onChange={(e) => setEditingFeed({...editingFeed, energy_content: e.target.value ? parseFloat(e.target.value) : undefined})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Gyártó</label>
                <input
                  type="text"
                  value={editingFeed.manufacturer || ''}
                  onChange={(e) => setEditingFeed({...editingFeed, manufacturer: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Leírás</label>
                <textarea
                  value={editingFeed.description || ''}
                  onChange={(e) => setEditingFeed({...editingFeed, description: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Minimum készlet riasztás (kg)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editingFeed.minimum_stock_alert_kg || ''}
                  onChange={(e) => setEditingFeed({...editingFeed, minimum_stock_alert_kg: e.target.value ? parseFloat(e.target.value) : undefined})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setEditingFeed(null)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Mégse
              </button>
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Mentés
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={deleteId !== null}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
        title="Takarmány törlése"
        message="Biztosan törölni szeretnéd ezt a takarmányt?"
        confirmLabel="Törlés"
      />
    </div>
  );
};

export default FeedManagement;